import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { WorkOrderTechnician } from '../../../models/technician.model';

@Component({
  selector: 'app-technician-multi-select-dropdown',
  standalone: true,
  imports: [CommonModule, NgbDropdownModule, TranslocoPipe],
  templateUrl: './technician-multi-select-dropdown.component.html',
  styleUrl: './technician-multi-select-dropdown.component.scss'
})
export class TechnicianMultiSelectDropdownComponent {
  private transloco = inject(TranslocoService);

  @Input() technicians: WorkOrderTechnician[] = [];
  @Input() selectedTechnicianIds: number[] = [];
  @Input() isLoading = false;
  @Input() disabled = false;
  @Input() placeholder = this.transloco.translate('workOrder.technicianDropdown.placeholder');

  @Output() selectedTechnicianIdsChange = new EventEmitter<number[]>();

  get label(): string {
    if (this.selectedTechnicianIds.length === 0) {
      return this.placeholder;
    }

    if (this.selectedTechnicianIds.length > 1) {
      return this.transloco.translate('workOrder.technicianDropdown.technicians');
    }

    const selectedId = this.selectedTechnicianIds[0];
    return this.technicians.find(technician => technician.technicianId === selectedId)?.technicianName
      ?? this.transloco.translate('workOrder.technicianDropdown.oneSelected');
  }

  get selectedCount(): number {
    return this.selectedTechnicianIds.length;
  }

  get showCountBadge(): boolean {
    return this.selectedTechnicianIds.length > 1;
  }

  isSelected(technicianId: number): boolean {
    return this.selectedTechnicianIds.includes(technicianId);
  }

  toggleTechnician(technician: WorkOrderTechnician): void {
    if (this.disabled || this.isLoading) {
      return;
    }

    const technicianId = technician.technicianId;
    const nextIds = this.isSelected(technicianId)
      ? this.selectedTechnicianIds.filter(selectedId => selectedId !== technicianId)
      : [...this.selectedTechnicianIds, technicianId];

    this.selectedTechnicianIds = nextIds;
    this.selectedTechnicianIdsChange.emit(nextIds);
  }

  onCheckboxClick(event: MouseEvent, technician: WorkOrderTechnician): void {
    event.stopPropagation();
    this.toggleTechnician(technician);
  }
}
